import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { fetchUsers } from '../store/slices/userSlice';
import { Role, User } from '../types';
import api from '../api/api';
import { POSITION_ROLES } from '../constants/positionRoles';
import { extractApiErrorMessage } from '../utils/apiError';
import { normalizeRoleNames } from '../utils/roleNames';
import { getOperatingContextErrorToast } from '../utils/operatingContextErrors';
import '../components/ResponsiveRecordTable.css';

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const getErrorStatus = (error: unknown): number | null => {
  if (!isRecord(error) || !isRecord(error.response)) return null;
  return typeof error.response.status === 'number' ? error.response.status : null;
};

const EmployeesPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { users, loading } = useAppSelector((s) => s.user);
  const [availableRoles, setAvailableRoles] = useState<Role[]>([]);
  const [editing, setEditing] = useState<User | null>(null);
  const [form, setForm] = useState({ firstName: '', lastName: '', phone: '', email: '', position: '', hireDate: '', baseRate: 0 });
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const [initialForm, setInitialForm] = useState(form);
  const [initialCredentials, setInitialCredentials] = useState(credentials);
  const [savedPosition, setSavedPosition] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingCredentials, setSavingCredentials] = useState(false);
  const [message, setMessage] = useState('');
  const [messageRole, setMessageRole] = useState<'alert' | 'status'>('alert');

  useEffect(() => {
    dispatch(fetchUsers());
  }, [dispatch]);

  useEffect(() => {
    api.get<Role[]>('/roles').then((res) => {
      setAvailableRoles(res.data.slice().sort((a, b) => a.name.localeCompare(b.name, 'uk')));
    });
  }, []);

  const openEdit = (u: User) => {
    const nextForm = {
      firstName: u.profile?.firstName ?? '',
      lastName: u.profile?.lastName ?? '',
      phone: u.profile?.phone ?? '',
      email: u.email ?? '',
      position: u.profile?.position ?? (u.roles[0] ?? ''),
      hireDate: u.profile?.hireDate?.split('T')[0] ?? '',
      baseRate: u.profile?.baseRate ?? 0,
    };
    const nextCredentials = {
      username: u.username ?? '',
      password: '',
    };
    setEditing(u);
    setMessage('');
    setForm(nextForm);
    setInitialForm(nextForm);
    setCredentials(nextCredentials);
    setInitialCredentials(nextCredentials);
    setSavedPosition(u.profile?.position ?? '');
  };

  const accountName = (u: User) => {
    const fullName = `${u.profile?.firstName ?? ''} ${u.profile?.lastName ?? ''}`.trim();
    return fullName ? `${u.username} (${fullName})` : u.username;
  };

  const confirmPositionChange = (u: User, position: string) => {
    const rolesToRemove = u.roles.filter(role =>
      POSITION_ROLES.some(positionRole => positionRole.toLowerCase() === role.toLowerCase())
      && role.toLowerCase() !== position.toLowerCase(),
    );
    const visibleRoles = normalizeRoleNames(rolesToRemove);
    const rolesText = visibleRoles.length > 0 ? visibleRoles.join(', ') : 'немає';
    return window.confirm(
      `Акаунт «${accountName(u)}». Посада стане «${position}»; буде знято ролі: ${rolesText}. Продовжити?`,
    );
  };

  const showActionError = (error: unknown, fallback: string) => {
    const status = getErrorStatus(error);
    if (!status || status === 401 || status === 409 || status >= 500 || getOperatingContextErrorToast(error)) return;
    setMessage(extractApiErrorMessage(error, fallback));
    setMessageRole('alert');
  };

  const handleRoleChange = async (u: User, role: string) => {
    if (!role || (u.profile?.position ?? '') === role) return;
    if (!confirmPositionChange(u, role)) return;
    setMessage('');
    try {
      await api.put(`/users/${u.id}/profile`, {
        firstName: u.profile?.firstName ?? '',
        lastName: u.profile?.lastName ?? '',
        phone: u.profile?.phone ?? '',
        email: u.email ?? '',
        position: role,
        hireDate: u.profile?.hireDate ?? null,
        baseRate: u.profile?.baseRate ?? 0,
      });
      dispatch(fetchUsers());
    } catch (error: unknown) {
      showActionError(error, 'Не вдалося змінити посаду.');
    }
  };

  const handleSaveProfile = async () => {
    if (!editing) return;
    if (form.position !== savedPosition && !confirmPositionChange(editing, form.position)) return;
    setSavingProfile(true);
    setMessage('');
    try {
      await api.put(`/users/${editing.id}/profile`, { ...form, hireDate: form.hireDate || null });
      setMessage('Профіль працівника збережено.');
      setMessageRole('status');
      setInitialForm(form);
      setSavedPosition(form.position);
      dispatch(fetchUsers());
    } catch (error: unknown) {
      showActionError(error, 'Не вдалося зберегти профіль.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSaveCredentials = async () => {
    if (!editing) return;
    setSavingCredentials(true);
    setMessage('');
    try {
      await api.put(`/users/${editing.id}/credentials`, {
        username: credentials.username,
        password: credentials.password || null,
      });
      setCredentials((prev) => ({ ...prev, password: '' }));
      setMessage('Логін/пароль оновлено.');
      setMessageRole('status');
      setInitialCredentials({ ...credentials, password: '' });
      dispatch(fetchUsers());
    } catch (error: unknown) {
      showActionError(error, 'Не вдалося оновити логін/пароль.');
    } finally {
      setSavingCredentials(false);
    }
  };

  const toggleActive = async (u: User) => {
    const action = u.isActive ? 'Деактивувати' : 'Активувати';
    if (!window.confirm(`${action} акаунт «${accountName(u)}»?`)) return;
    setMessage('');
    try {
      await api.patch(`/users/${u.id}/active`, !u.isActive);
      dispatch(fetchUsers());
    } catch (error: unknown) {
      showActionError(error, `Не вдалося ${u.isActive ? 'деактивувати' : 'активувати'} акаунт.`);
    }
  };

  const removeRole = async (u: User, role: string) => {
    if (!window.confirm(`Видалити роль «${normalizeRoleNames([role])[0]}» для акаунта «${accountName(u)}»?`)) return;
    setMessage('');
    try {
      await api.delete(`/users/${u.id}/roles/${encodeURIComponent(role)}`);
      dispatch(fetchUsers());
    } catch (error: unknown) {
      showActionError(error, 'Не вдалося видалити роль.');
    }
  };

  const closeEditor = () => {
    const hasUnsavedChanges = JSON.stringify(form) !== JSON.stringify(initialForm)
      || JSON.stringify(credentials) !== JSON.stringify(initialCredentials);
    if (hasUnsavedChanges && !window.confirm('Є незбережені зміни. Закрити без збереження?')) return;
    setEditing(null);
  };

  const cell: React.CSSProperties = { padding: '10px 12px', borderBottom: '1px solid var(--crm-table-row-border)' };
  const btn = (tone: 'info' | 'success' | 'blocked' | 'muted' | 'danger'): React.CSSProperties => ({
    background: `var(--crm-${tone === 'muted' ? 'surface-alt' : `${tone}-bg`})`,
    color: `var(--crm-${tone === 'muted' ? 'text-muted' : tone})`,
    border: `1px solid var(--crm-${tone === 'muted' ? 'border' : tone})`,
    borderRadius: 'var(--crm-radius-sm)',
    padding: '4px 10px', cursor: 'pointer', fontSize: '13px', marginLeft: '4px',
  });
  const labelStyle: React.CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, fontWeight: 600, fontSize: 14 };
  const inputStyle: React.CSSProperties = { boxSizing: 'border-box', width: '100%', minHeight: 44, padding: '8px 10px', border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-sm)', background: 'var(--crm-surface)', color: 'var(--crm-text)', fontSize: 16 };

  return (
    <div className="page-shell page-shell--wide employee-accounts-page">
      <h2 style={{ color: 'var(--crm-text)', borderBottom: '2px solid var(--crm-info)', paddingBottom: '8px' }}>
        Акаунти, ролі, доступ
      </h2>
      <div className="employee-accounts-page__navigation">
        <Link
          to="/management/employees"
          style={{ background: 'var(--crm-info-bg)', color: 'var(--crm-info)', border: '1px solid var(--crm-info)', padding: '8px 14px', borderRadius: 'var(--crm-radius-sm)', textDecoration: 'none' }}
        >
          Перейти в HR: персонал, оцінка, підрозділи
        </Link>
      </div>

      {message && !editing && <div role={messageRole} style={{ margin: '0 0 12px', color: 'var(--crm-text)' }}>{message}</div>}
      {loading && <p>Завантаження...</p>}

      <div className="responsive-record-table-container">
      <table className="crm-table responsive-record-table" aria-label="Акаунти, ролі, доступ">
        <thead style={{ background: 'var(--crm-navbar-dropdown-bg)', color: 'var(--crm-primary-contrast)' }}>
          <tr>
            {['Логін', 'Email', 'ПІБ', 'Роль (посада)', 'Телефон', 'Ролі', 'Статус', 'Дії'].map(h => (
              <th scope="col" key={h}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {users.map((u, i) => (
            <tr key={u.id} style={{ background: i % 2 === 0 ? 'var(--crm-surface-alt)' : 'var(--crm-surface)' }}>
              <td data-label="Логін" style={cell}>{u.username}</td>
              <td data-label="Email" style={cell}>{u.email}</td>
              <td data-label="ПІБ" style={cell}>{u.profile ? `${u.profile.firstName} ${u.profile.lastName}`.trim() || '—' : '—'}</td>
              <td data-label="Роль (посада)" style={cell}>{u.profile?.position || '—'}</td>
              <td data-label="Телефон" style={cell}>{u.profile?.phone || '—'}</td>
              <td data-label="Ролі" style={cell}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                  {u.roles.map(r => (
                    <span key={r} style={{ background: 'var(--crm-blocked-bg)', color: 'var(--crm-blocked)', borderRadius: '12px', padding: '2px 8px', fontSize: '12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
                      {r}
                      <button type="button" className="employee-accounts-page__remove-role"
                        aria-label={`Видалити роль ${r} для ${u.username}`} onClick={() => removeRole(u, r)}>×</button>
                    </span>
                  ))}
                  <select
                    aria-label={`Роль для ${u.username}`}
                    value={u.profile?.position ?? ''}
                    onChange={e => { if (e.target.value) { handleRoleChange(u, e.target.value); } }}
                    style={{ fontSize: '12px', padding: '2px 4px', borderRadius: 'var(--crm-radius-sm)', border: '1px solid var(--crm-border)' }}>
                    <option value="">Змінити посаду…</option>
                    {availableRoles.filter((r) => !u.roles.includes(r.name)).map((r) => <option key={r.id} value={r.name}>{r.name}</option>)}
                  </select>
                </div>
              </td>
              <td data-label="Статус" style={cell}>
                <span style={{ background: u.isActive ? 'var(--crm-success-bg)' : 'var(--crm-danger-bg)', color: u.isActive ? 'var(--crm-success)' : 'var(--crm-danger)', padding: '2px 8px', borderRadius: '12px', fontSize: '13px' }}>
                  {u.isActive ? 'Активний' : 'Неактивний'}
                </span>
              </td>
              <td data-label="Дії" style={cell}>
                <div className="responsive-record-table__actions">
                <button style={btn('info')} onClick={() => openEdit(u)}>Редагувати</button>
                <button style={btn(u.isActive ? 'danger' : 'success')} onClick={() => toggleActive(u)}>
                  {u.isActive ? 'Деактивувати' : 'Активувати'}
                </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {!loading && users.length === 0 && <p role="status">Акаунтів поки немає.</p>}
      </div>

      {editing && (
        <div className="admin-employee-edit-overlay" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div className="admin-employee-edit-modal" style={{ background: 'var(--crm-surface)', color: 'var(--crm-text)', border: '1px solid var(--crm-border)', padding: '24px', borderRadius: 'var(--crm-radius-lg)', width: 'min(560px, 100%)', boxSizing: 'border-box', maxHeight: '90vh', overflowY: 'auto', boxShadow: 'var(--crm-shadow-sm)' }}>
            <h3 style={{ margin: '0 0 14px', color: 'var(--crm-text)' }}>Редагування: {editing.username}</h3>
            {message && <div role={messageRole} style={{ marginBottom: 10, color: 'var(--crm-text)', fontSize: 14 }}>{message}</div>}

            <div style={{ padding: 10, border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)', marginBottom: 12 }}>
              <h4 style={{ marginTop: 0, marginBottom: 8 }}>Профіль</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 8 }}>
                <label htmlFor="employee-first-name" style={labelStyle}>Ім'я
                  <input id="employee-first-name" style={inputStyle} value={form.firstName} onChange={e => setForm({ ...form, firstName: e.target.value })} />
                </label>
                <label htmlFor="employee-last-name" style={labelStyle}>Прізвище
                  <input id="employee-last-name" style={inputStyle} value={form.lastName} onChange={e => setForm({ ...form, lastName: e.target.value })} />
                </label>
                <label htmlFor="employee-phone" style={labelStyle}>Телефон
                  <input id="employee-phone" style={inputStyle} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
                </label>
                <label htmlFor="employee-email" style={labelStyle}>Email
                  <input id="employee-email" style={inputStyle} value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
                </label>
                <label htmlFor="employee-position" style={labelStyle}>Посада
                  <select id="employee-position" style={inputStyle} value={form.position} onChange={e => setForm({ ...form, position: e.target.value })}>
                    <option value="">Роль (посада)</option>
                    {availableRoles.map((role) => <option key={role.id} value={role.name}>{role.name}</option>)}
                  </select>
                </label>
                <div>
                  <label htmlFor="employee-hire-date" style={labelStyle}>Дата прийняття
                    <input id="employee-hire-date" style={inputStyle} aria-describedby="employee-hire-date-hint" type="date" value={form.hireDate} onChange={e => setForm({ ...form, hireDate: e.target.value })} />
                  </label>
                  <span id="employee-hire-date-hint" style={{ display: 'block', marginTop: 4, color: 'var(--crm-text-muted)', fontSize: 12 }}>Дата, з якої працівник працює в компанії.</span>
                </div>
                <label htmlFor="employee-base-rate" style={labelStyle}>Базова ставка
                  <input id="employee-base-rate" style={inputStyle} type="number" value={form.baseRate} onChange={e => setForm({ ...form, baseRate: Number(e.target.value) })} />
                </label>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                <button type="button" onClick={handleSaveProfile} disabled={savingProfile} style={{ ...btn('success'), marginLeft: 0, minHeight: 44 }}>
                  {savingProfile ? 'Збереження…' : 'Зберегти роль/профіль'}
                </button>
              </div>
            </div>

            <div style={{ padding: 10, border: '1px solid var(--crm-border)', borderRadius: 'var(--crm-radius-md)' }}>
              <h4 style={{ marginTop: 0, marginBottom: 8 }}>Доступ в систему</h4>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 220px), 1fr))', gap: 8 }}>
                <label htmlFor="employee-username" style={labelStyle}>Логін
                  <input id="employee-username" style={inputStyle} value={credentials.username} onChange={e => setCredentials({ ...credentials, username: e.target.value })} />
                </label>
                <label htmlFor="employee-password" style={labelStyle}>Новий пароль
                  <input id="employee-password" style={inputStyle} type="password" aria-describedby="employee-password-hint" value={credentials.password} onChange={e => setCredentials({ ...credentials, password: e.target.value })} />
                </label>
              </div>
              <div id="employee-password-hint" style={{ fontSize: 12, color: 'var(--crm-text-muted)', marginTop: 6 }}>
                Якщо пароль залишити порожнім — оновиться лише логін.
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 10 }}>
                <button type="button" onClick={handleSaveCredentials} disabled={savingCredentials} style={{ ...btn('muted'), marginLeft: 0, minHeight: 44 }}>
                  {savingCredentials ? 'Збереження…' : 'Зберегти логін/пароль'}
                </button>
              </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 14 }}>
              <button type="button" onClick={closeEditor} style={{ ...btn('muted'), padding: '8px 16px', marginLeft: 0, minHeight: 44 }}>Закрити</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeesPage;
